import { httpStatusOf } from '@/app/live-election/engine/capability';
import { coreApi } from '@/lib/api-client';

import {
	HELP_CALLOUT_TYPES,
	type HelpArticleState,
	type HelpCalloutType,
	type HelpStateFilter,
	type IHelpAdminArticle,
	type IHelpAdminArticleList,
	type IHelpAdminArticleRow,
	type IHelpArticle,
	type IHelpArticleSummary,
	type IHelpCallout,
	type IHelpCapabilities,
	type IHelpContent,
	type IHelpDraftBody,
	type IHelpFieldError,
	type IHelpHighlight,
	type IHelpModuleContext,
	type IHelpModuleNode,
	type IHelpModuleSync,
	type IHelpScreenshot,
	type IHelpSearchResult,
	type IHelpStep,
	type IHelpTree,
	type IHelpVersion,
	type IHelpVisibilityPreview,
} from './help-centre.types';

/**
 * api.core Help Centre client (contract: backend `docs/help-centre-api.md`). Every reader normalises
 * what it receives, because the backend is routinely older than this frontend (an absent field is a
 * third state, never `false`) and an HTML fallback page answering 200 must read as "no Help Centre",
 * not as an empty manual.
 */

export const HELP_BASE = '/api/v1/HelpCentre';

/** The server has no Help Centre at all (older api.core build, or the route answered something else). */
export class HelpCentreUnavailableError extends Error {
	constructor() {
		super('This server has no Help Centre');
	}
}

/** Another write landed after this editor loaded the guide (`STALE_ROW_VERSION` / `DRAFT_CHANGED`). */
export class HelpDraftConflictError extends Error {
	constructor() {
		super('Someone else saved this guide since you opened it');
	}
}

/** 404/405 = absent; 401/403/429/400 will not fix themselves on an immediate retry. */
export const retryHelp = (count: number, error: unknown) => {
	if (error instanceof HelpCentreUnavailableError) return false;
	const s = httpStatusOf(error);
	return s !== 404 && s !== 405 && s !== 403 && s !== 401 && s !== 429 && s !== 400 && count < 2;
};

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

const unwrap = (body: unknown): unknown => (isObject(body) && 'data' in body ? body.data : body);

/** The failure `data.code` (`STALE_ROW_VERSION`, `DRAFT_INCOMPLETE`, …) — switch on this, never on `message`. */
export const helpErrorCode = (error: unknown): string | null => {
	const body = (error as { response?: { data?: unknown } } | null)?.response?.data;
	const data = isObject(body) ? body.data : null;
	return isObject(data) && typeof data.code === 'string' ? data.code : null;
};

/** Field errors from a 400 (`INVALID_CONTENT`, `VALIDATION`, `MISSING_FIELDS`, `VALUE_TOO_LONG`). */
export const helpFieldErrors = (error: unknown): IHelpFieldError[] => {
	const body = (error as { response?: { data?: unknown } } | null)?.response?.data;
	const data = isObject(body) ? body.data : null;
	if (!isObject(data) || !Array.isArray(data.errors)) return [];
	return data.errors
		.filter(isObject)
		.map((e) => ({ field: normaliseFieldPath(str(e.field)), message: str(e.message) }))
		.filter((e) => e.message);
};

/** `Steps[1].BodyMd` → `steps[1].bodyMd`: camel-case every segment, whatever casing the validator used. */
export const normaliseFieldPath = (path: string) =>
	path
		.split('.')
		.map((seg) => (seg ? seg[0].toLowerCase() + seg.slice(1) : seg))
		.join('.');

const str = (v: unknown, fallback = ''): string => {
	if (typeof v !== 'string') return fallback;
	// The backend has a history of serialising unset strings as the literal "null".
	return v === 'null' || v === 'undefined' ? fallback : v;
};

const optStr = (v: unknown): string | null => {
	const s = str(v);
	return s ? s : null;
};

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

const toScreenshot = (v: unknown): IHelpScreenshot | null => {
	if (!isObject(v)) return null;
	const url = str(v.url);
	if (!url) return null;
	return { url, alt: str(v.alt), width: num(v.width), height: num(v.height) };
};

export const toStep = (v: unknown): IHelpStep => {
	const o = isObject(v) ? v : {};
	return {
		title: str(o.title),
		bodyMd: str(o.bodyMd),
		screenshot: toScreenshot(o.screenshot),
		anchor: optStr(o.anchor),
		expect: optStr(o.expect),
		roleNote: optStr(o.roleNote),
	};
};

const toCallout = (v: unknown): IHelpCallout | null => {
	if (!isObject(v)) return null;
	const type = str(v.type).toLowerCase() as HelpCalloutType;
	const text = str(v.text);
	if (!HELP_CALLOUT_TYPES.includes(type) || !text) return null;
	return { type, text };
};

const toCallouts = (v: unknown) =>
	arr(v)
		.map(toCallout)
		.filter((c): c is IHelpCallout => c !== null);

const toStrings = (v: unknown) =>
	arr(v)
		.map((s) => str(s).trim())
		.filter(Boolean);

export const toSummary = (v: unknown, module?: { key: string; title: string }): IHelpArticleSummary | null => {
	if (!isObject(v)) return null;
	const slug = str(v.slug);
	if (!slug) return null;
	return {
		slug,
		title: str(v.title, slug),
		summary: str(v.summary),
		kind: str(v.kind, 'Guide'),
		moduleKey: str(v.moduleKey, module?.key ?? ''),
		moduleTitle: optStr(v.moduleTitle) ?? module?.title,
		journeyKey: optStr(v.journeyKey),
		estMinutes: num(v.estMinutes),
		stepCount: num(v.stepCount),
		updatedAt: optStr(v.updatedAt),
	};
};

const toSummaries = (v: unknown, module?: { key: string; title: string }) =>
	arr(v)
		.map((a) => toSummary(a, module))
		.filter((s): s is IHelpArticleSummary => s !== null);

const toModule = (v: unknown): IHelpModuleNode | null => {
	if (!isObject(v)) return null;
	const key = str(v.key);
	if (!key) return null;
	const title = str(v.title, key);
	return {
		key,
		title,
		section: str(v.section),
		journeyKey: optStr(v.journeyKey),
		routeUrl: optStr(v.routeUrl),
		parentKey: optStr(v.parentKey),
		articles: toSummaries(v.articles, { key, title }),
	};
};

const toTree = (d: unknown): IHelpTree => {
	const modules = (Array.isArray(d) ? d : isObject(d) ? arr(d.modules) : [])
		.map(toModule)
		.filter((m): m is IHelpModuleNode => m !== null);
	const counted = modules.reduce((n, m) => n + m.articles.length, 0);
	return { totalCount: isObject(d) ? (num(d.totalCount) ?? counted) : counted, modules };
};

/** Capability handshake. Any failure to get a well-formed answer means "no Help Centre here". */
export const fetchHelpCapabilities = async (signal?: AbortSignal): Promise<IHelpCapabilities> => {
	try {
		const { data } = await coreApi.get(`${HELP_BASE}/capabilities`, { signal });
		const d = unwrap(data);
		if (!isObject(d) || typeof d.enabled !== 'boolean') throw new HelpCentreUnavailableError();
		return { enabled: d.enabled, search: str(d.search, 'basic') };
	} catch (error) {
		if (error instanceof HelpCentreUnavailableError) throw error;
		const s = httpStatusOf(error);
		if (s === 404 || s === 405) throw new HelpCentreUnavailableError();
		throw error;
	}
};

export const fetchHelpTree = async (signal?: AbortSignal): Promise<IHelpTree> => {
	const { data } = await coreApi.get(`${HELP_BASE}/tree`, { signal });
	const d = unwrap(data);
	if (!isObject(d) && !Array.isArray(d)) throw new HelpCentreUnavailableError();
	return toTree(d);
};

export const toArticle = (v: unknown): IHelpArticle => {
	const o = isObject(v) ? v : {};
	const module = isObject(o.module) ? o.module : {};
	return {
		slug: str(o.slug),
		moduleKey: str(module.key, str(o.moduleKey)),
		moduleTitle: str(module.title, str(o.moduleTitle)),
		kind: str(o.kind, 'Guide'),
		title: str(o.title),
		summary: str(o.summary),
		steps: arr(o.steps).map(toStep),
		prerequisites: toStrings(o.prerequisites),
		callouts: toCallouts(o.callouts),
		estMinutes: num(o.estMinutes),
		updatedAt: optStr(o.updatedAt) ?? optStr(o.publishedAt),
	};
};

/** 404 here means "not found OR not visible to you" — the server deliberately does not say which. */
export const fetchHelpArticle = async (slug: string, signal?: AbortSignal): Promise<IHelpArticle> => {
	const { data } = await coreApi.get(`${HELP_BASE}/articles/${encodeURIComponent(slug)}`, { signal });
	const d = unwrap(data);
	if (!isObject(d) || !str(d.slug)) throw new HelpCentreUnavailableError();
	return toArticle(d);
};

/** The server accepts 2–100 characters after collapsing whitespace; anything else is a 400. */
export const normaliseHelpQuery = (q: string) => q.replace(/\s+/g, ' ').trim().slice(0, 100);

const toHighlights = (v: unknown, snippet: string): IHelpHighlight[] =>
	arr(v)
		.filter(isObject)
		.map((h) => ({ start: num(h.start) ?? -1, length: num(h.length) ?? 0 }))
		.filter((h) => h.start >= 0 && h.length > 0 && h.start + h.length <= snippet.length)
		.sort((a, b) => a.start - b.start);

export const fetchHelpSearch = async (
	q: string,
	page: number,
	pageSize: number,
	signal?: AbortSignal
): Promise<IHelpSearchResult> => {
	const params = new URLSearchParams({ q: normaliseHelpQuery(q), pageNumber: String(page), pageSize: String(pageSize) });
	const { data } = await coreApi.get(`${HELP_BASE}/search?${params.toString()}`, { signal });
	// Flat paged envelope: the hits are `data`, the totals sit beside it at the root.
	const items = arr(unwrap(data));
	const total = isObject(data) ? num(data.totalCount) : null;
	return {
		items: items
			.filter(isObject)
			.map((h) => {
				const snippet = str(h.snippet);
				return {
					slug: str(h.slug),
					title: str(h.title),
					summary: str(h.summary),
					moduleKey: str(h.moduleKey),
					moduleTitle: str(h.moduleTitle),
					snippet,
					highlights: toHighlights(h.highlights, snippet),
				};
			})
			.filter((h) => !!h.slug),
		totalCount: total ?? items.length,
		page,
		pageSize,
	};
};

export const fetchModuleContext = async (key: string, signal?: AbortSignal): Promise<IHelpModuleContext> => {
	try {
		const { data } = await coreApi.get(`${HELP_BASE}/modules/${encodeURIComponent(key)}/context`, { signal });
		const d = unwrap(data);
		if (!isObject(d)) return { module: null, articles: [] };
		const m = isObject(d.module) ? d.module : null;
		const module = m ? { key: str(m.key, key), title: str(m.title, key), routeUrl: optStr(m.routeUrl) } : null;
		return { module, articles: toSummaries(d.articles, module ?? undefined) };
	} catch (error) {
		// An unknown (or invisible) module has no guides — that is an answer, not a failure.
		if (httpStatusOf(error) === 404) return { module: null, articles: [] };
		throw error;
	}
};

// ── Editor ────────────────────────────────────────────────────

const ADMIN = `${HELP_BASE}/admin`;

const deriveState = (isPublished: boolean, everPublished: boolean): HelpArticleState =>
	isPublished ? 'Published' : everPublished ? 'Unpublished' : 'Draft';

const toAdminRow = (v: unknown): IHelpAdminArticleRow | null => {
	if (!isObject(v)) return null;
	const id = num(v.id);
	if (id === null) return null;
	const isPublished = v.isPublished === true;
	return {
		id,
		slug: str(v.slug),
		title: str(v.title, str(v.slug)),
		moduleKey: str(v.moduleKey),
		moduleTitle: str(v.moduleTitle, str(v.moduleKey)),
		kind: str(v.kind, 'Guide'),
		state: deriveState(isPublished, num(v.publishedVersionNumber) !== null),
		hasDraft: v.hasDraft === true,
		updatedAt: optStr(v.updatedAt),
	};
};

export const fetchAdminArticles = async (
	filter: { moduleKey?: string; state?: HelpStateFilter; page: number; pageSize: number },
	signal?: AbortSignal
): Promise<IHelpAdminArticleList> => {
	const params = new URLSearchParams({ pageNumber: String(filter.page), pageSize: String(filter.pageSize) });
	if (filter.moduleKey) params.set('moduleKey', filter.moduleKey);
	if (filter.state) params.set('state', filter.state);
	const { data } = await coreApi.get(`${ADMIN}/articles?${params.toString()}`, { signal });
	const rows = arr(unwrap(data))
		.map(toAdminRow)
		.filter((r): r is IHelpAdminArticleRow => r !== null);
	return { items: rows, totalCount: (isObject(data) ? num(data.totalCount) : null) ?? rows.length };
};

const toContent = (v: unknown): IHelpContent => {
	const o = isObject(v) ? v : {};
	return {
		title: str(o.title),
		summary: str(o.summary),
		steps: arr(o.steps).map(toStep),
		prerequisites: toStrings(o.prerequisites),
		callouts: toCallouts(o.callouts),
		estMinutes: num(o.estMinutes),
		extraRequiredActions: toStrings(o.extraRequiredActions),
		requiredRoles: toStrings(o.requiredRoles),
	};
};

export const toAdminArticle = (v: unknown): IHelpAdminArticle => {
	const o = isObject(v) ? v : {};
	const module = isObject(o.module) ? o.module : {};
	const draft = isObject(o.draft) ? o.draft : null;
	const published = isObject(o.published) ? o.published : null;
	const isPublished = !!published && str(published.status, 'Published') === 'Published';
	return {
		id: num(o.id) ?? 0,
		slug: str(o.slug),
		moduleKey: str(module.key, str(o.moduleKey)),
		moduleTitle: str(module.title, str(o.moduleTitle)),
		kind: str(o.kind, 'Guide'),
		state: deriveState(isPublished, !!published),
		rowVersion: num(o.rowVersion) ?? 0,
		draftVersionId: draft ? num(draft.id) : null,
		// The draft carries the editable content; with no draft the editor starts from the live copy.
		content: toContent(draft ?? published ?? {}),
		hasDraft: !!draft,
		isPublished,
		publishedAt: published ? optStr(published.publishedAt) : null,
		updatedAt: optStr(o.updatedAt),
	};
};

export const fetchAdminArticle = async (id: number, signal?: AbortSignal): Promise<IHelpAdminArticle> => {
	const { data } = await coreApi.get(`${ADMIN}/articles/${id}`, { signal });
	return toAdminArticle(unwrap(data));
};

export const createAdminArticle = async (body: { moduleKey: string; title: string; slug: string; kind: string }) => {
	const { data } = await coreApi.post(`${ADMIN}/articles`, body);
	return toAdminArticle(unwrap(data));
};

const asConflict = (error: unknown) => {
	const code = helpErrorCode(error);
	if (httpStatusOf(error) === 409 && (code === null || code === 'STALE_ROW_VERSION' || code === 'DRAFT_CHANGED')) {
		return new HelpDraftConflictError();
	}
	return error;
};

export const saveDraft = async (id: number, body: IHelpDraftBody) => {
	try {
		const { data } = await coreApi.put(`${ADMIN}/articles/${id}/draft`, body);
		return toAdminArticle(unwrap(data));
	} catch (error) {
		throw asConflict(error);
	}
};

export const publishArticle = async (id: number, draftVersionId: number, rowVersion: number) => {
	try {
		const { data } = await coreApi.post(`${ADMIN}/articles/${id}/publish`, { draftVersionId, rowVersion });
		return toAdminArticle(unwrap(data));
	} catch (error) {
		throw asConflict(error);
	}
};

export const unpublishArticle = async (id: number, rowVersion: number) => {
	try {
		const { data } = await coreApi.post(`${ADMIN}/articles/${id}/unpublish`, { rowVersion });
		return toAdminArticle(unwrap(data));
	} catch (error) {
		throw asConflict(error);
	}
};

export const fetchVersions = async (id: number, signal?: AbortSignal): Promise<IHelpVersion[]> => {
	const { data } = await coreApi.get(`${ADMIN}/articles/${id}/versions`, { signal });
	return arr(unwrap(data))
		.filter(isObject)
		.map((v) => ({
			id: num(v.id) ?? 0,
			versionNumber: num(v.versionNumber) ?? 0,
			state: str(v.status, str(v.state)),
			title: str(v.title),
			changeNote: optStr(v.changeNote),
			createdAt: optStr(v.createdAt),
			publishedAt: optStr(v.publishedAt),
		}));
};

/** Copies version `versionNumber` into the draft. `rowVersion` is REQUIRED — a restore overwrites the draft. */
export const restoreVersion = async (id: number, versionNumber: number, rowVersion: number) => {
	try {
		const { data } = await coreApi.post(`${ADMIN}/articles/${id}/versions/${versionNumber}/restore`, { rowVersion });
		return toAdminArticle(unwrap(data));
	} catch (error) {
		throw asConflict(error);
	}
};

export interface IHelpSyncResult {
	created: number;
	updated: number;
	unchanged: number;
	/** Stored modules the posted menu no longer has. Reported, never deleted. */
	notInMenu: string[];
}

export const syncModules = async (modules: IHelpModuleSync[]): Promise<IHelpSyncResult> => {
	const { data } = await coreApi.put(`${ADMIN}/modules/sync`, { modules });
	const d = unwrap(data);
	const o = isObject(d) ? d : {};
	return {
		created: arr(o.created).length,
		updated: arr(o.updated).length,
		unchanged: num(o.unchanged) ?? 0,
		notInMenu: toStrings(o.notInMenu),
	};
};

/** What a freshly logged-in holder of these roles would see — the server's own expansion and filter. */
export const previewVisibility = async (roleNames: string[]): Promise<IHelpVisibilityPreview> => {
	const { data } = await coreApi.post(`${ADMIN}/preview-visibility`, { roleNames });
	const d = unwrap(data);
	const o = isObject(d) ? d : {};
	return { roles: toStrings(o.roles), unknownRoles: toStrings(o.unknownRoles), tree: toTree(o.tree) };
};

/**
 * Upload one screenshot to the Help Centre's own media store (api.core relays it to MediaStash and
 * serves it back anonymously at `…/HelpCentre/media/{id}`). Returns the URL to save on the step.
 * The same bytes uploaded twice come back as the same URL.
 */
export const uploadHelpMedia = async (
	file: File,
	onProgress?: (percent: number) => void,
	signal?: AbortSignal
): Promise<string> => {
	const form = new FormData();
	form.append('file', file);
	const { data } = await coreApi.post(`${ADMIN}/media`, form, {
		signal,
		onUploadProgress: (e) => {
			if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
		},
	});
	const d = unwrap(data);
	const url = isObject(d) && typeof d.url === 'string' ? d.url : '';
	if (!url) throw new Error('The upload finished but returned no image address.');
	return url;
};
